import React, { useState, useEffect, useCallback } from "react";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_green.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getAdvertisementBannerById,
  addAdvertisementBanner,
  updateAdvertisementBanner,
} from "../../../services/advertisementBannersService";
import { getContentSectionsList } from "../../../services/contentSectionsService";
import { getContentsList } from "../../../services/contentsService";
import { validateAdvertisementBanner } from "../../../utils/validation";
import {
  PAGE_TYPE_OPTIONS,
  ADVERTISEMENT_POSITION_OPTIONS,
  PAGE_TYPES,
} from "./advertisementConstants";
import {
  mapAdvertisementResponse,
  buildAdvertisementFormData,
  mapDropdownOptions,
  pageTypeRequiresReference,
} from "./advertisementUtils";
import { handleErrors } from "../../../utils/errorHandler";
import FormSideDrawer from "../../Common/FormSideDrawer/FormSideDrawer";
import ImageUpload from "../../Common/ImageUpload/ImageUpload";
import { useMediaLibraryIntegration } from "../../MediaLibrary/MediaSourcePicker";

const INITIAL_FORM_STATE = {
  advertisementName: "",
  redirectUrl: "",
  advertisementPosition: "",
  pageType: "",
  referenceId: "",
  displayOrder: "",
  startDate: "",
  endDate: "",
  imageFile: null,
  imagePreview: "",
  advertisementImageUrl: "",
};

const INITIAL_ERRORS_STATE = {
  advertisementName: "",
  advertisementImage: "",
  redirectUrl: "",
  advertisementPosition: "",
  pageType: "",
  referenceId: "",
  displayOrder: "",
  startDate: "",
  endDate: "",
};

const DATETIME_PICKER_OPTIONS = {
  enableTime: true,
  dateFormat: "Y-m-d H:i",
  time_24hr: true,
  monthSelectorType: "static",
};

export const AddAdvertisement = ({
  editMode = false,
  initialData = {},
  onSuccess,
  setSelectedAdvertisement,
  setEditMode,
  isOpen = false,
  onClose,
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState(INITIAL_ERRORS_STATE);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [sections, setSections] = useState([]);
  const [contents, setContents] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState(null);

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_STATE);
    setErrors(INITIAL_ERRORS_STATE);
    setSelectedMedia(null);
  }, []);

  const closeDrawer = useCallback(() => {
    resetForm();
    setSelectedAdvertisement?.(null);
    setEditMode?.(false);
    onClose?.();
  }, [resetForm, setSelectedAdvertisement, setEditMode, onClose]);

  useEffect(() => {
    const loadReferences = async () => {
      try {
        const [secRes, contRes] = await Promise.all([
          getContentSectionsList(),
          getContentsList(),
        ]);
        const secList = secRes.data?.result || secRes.data || [];
        const contList = contRes.data?.result || contRes.data || [];
        setSections(
          mapDropdownOptions(
            Array.isArray(secList) ? secList : [],
            "sectionName",
            "SectionName"
          )
        );
        setContents(
          mapDropdownOptions(
            Array.isArray(contList) ? contList : [],
            "title",
            "Title"
          )
        );
      } catch (error) {
        handleErrors(error);
      }
    };
    loadReferences();
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const fetchData = async () => {
      const adId = initialData?.id ?? initialData?.Id;
      if (editMode && adId) {
        try {
          const data = await getAdvertisementBannerById(adId);
          setFormData(mapAdvertisementResponse(data));
          setSelectedMedia(null);
        } catch (error) {
          handleErrors(error);
        }
      } else {
        setFormData(INITIAL_FORM_STATE);
        setSelectedMedia(null);
      }
      setErrors(INITIAL_ERRORS_STATE);
    };
    fetchData();
  }, [editMode, initialData, isOpen]);

  const showReferenceDropdown = pageTypeRequiresReference(formData.pageType);

  const referenceOptions =
    formData.pageType === PAGE_TYPES.SECTION
      ? sections
      : formData.pageType === PAGE_TYPES.CONTENT
        ? contents
        : [];

  const referenceLabel =
    formData.pageType === PAGE_TYPES.SECTION
      ? "Section"
      : formData.pageType === PAGE_TYPES.CONTENT
        ? "Content"
        : "Reference";

  const handleInputChange = ({ target: { name, value } }) => {
    if (name === "pageType") {
      setFormData((prev) => ({
        ...prev,
        pageType: value,
        referenceId: pageTypeRequiresReference(value) ? prev.referenceId : "",
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleDisplayOrderChange = (e) => {
    const { value } = e.target;
    if (value === "" || /^[1-9]\d*$/.test(value)) {
      setFormData((prev) => ({ ...prev, displayOrder: value }));
      setErrors((prev) => ({ ...prev, displayOrder: "" }));
    }
  };

  const handleDisplayOrderKeyDown = (e) => {
    if (["e", "E", "+", "-", "."].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleDateChange = (name, dateStr) => {
    setFormData((prev) => ({ ...prev, [name]: dateStr || "" }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleMediaSelect = (media) => {
    const url = media?.mediaUrl || media?.thumbnailUrl || "";
    if (!url) return;
    setFormData((prev) => ({
      ...prev,
      imageFile: null,
      imagePreview: url,
      advertisementImageUrl: url,
    }));
    setSelectedMedia(media);
    setErrors((prev) => ({ ...prev, advertisementImage: "" }));
  };

  // Remove only the Media Library selection (mediaUrl + preview + asset).
  const handleMediaRemove = () => {
    setFormData((prev) => ({
      ...prev,
      imageFile: null,
      imagePreview: "",
      advertisementImageUrl: "",
    }));
    setSelectedMedia(null);
    setErrors((prev) => ({ ...prev, advertisementImage: "" }));
  };

  const imageLibrary = useMediaLibraryIntegration({
    media: selectedMedia,
    imageUrl: formData.advertisementImageUrl,
    defaultKeyword: formData.advertisementName,
    onSelect: handleMediaSelect,
    onRemove: handleMediaRemove,
  });

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const { valid, errors: validationErrors } = validateAdvertisementBanner(
        formData,
        editMode
      );
      setErrors(validationErrors);

      if (!valid) return;

      const adId = editMode
        ? (initialData?.id ?? initialData?.Id)
        : undefined;
      const payload = buildAdvertisementFormData(formData, editMode, adId);

      try {
        setIsButtonDisabled(true);
        const action = editMode
          ? updateAdvertisementBanner
          : addAdvertisementBanner;
        const response = await action(payload);

        if (response?.data?.isSuccess === false) {
          toast.error(
            response.data.message || "Request failed. Please try again."
          );
          return;
        }

        toast.success(
          `Advertisement ${editMode ? "updated" : "added"} successfully!`
        );
        resetForm();
        onSuccess?.();
        setEditMode?.(false);
        setSelectedAdvertisement?.(null);
        onClose?.();
      } catch (error) {
        handleErrors(error);
      } finally {
        setIsButtonDisabled(false);
      }
    },
    [
      formData,
      editMode,
      initialData,
      onSuccess,
      setEditMode,
      setSelectedAdvertisement,
      onClose,
      resetForm,
    ]
  );

  return (
    <FormSideDrawer
      isOpen={isOpen}
      onClose={closeDrawer}
      title={editMode ? "Update Advertisement" : "Add Advertisement"}
      subtitle={
        editMode
          ? "Edit advertisement details and save changes"
          : "Fill in the details to create a new advertisement"
      }
      icon={editMode ? "ri-pencil-line" : "ri-advertisement-line"}
      width="lg"
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={closeDrawer}
            disabled={isButtonDisabled}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="advertisement-side-form"
            className="btn btn-secondary"
            disabled={isButtonDisabled}
          >
            {isButtonDisabled
              ? editMode
                ? "Updating..."
                : "Saving..."
              : editMode
                ? "Update"
                : "Save"}
          </button>
        </>
      }
    >
      <form id="advertisement-side-form" onSubmit={handleSubmit} method="POST">
        <div className="row g-3">
          <div className="col-12">
            <label htmlFor="advertisementName" className="form-label">
              Advertisement Name <span className="required-field">*</span>
            </label>
            <input
              type="text"
              name="advertisementName"
              id="advertisementName"
              placeholder="Enter advertisement name"
              value={formData.advertisementName}
              onChange={handleInputChange}
              className={`form-control ${
                errors.advertisementName ? "is-invalid" : ""
              }`}
            />
            {errors.advertisementName && (
              <div className="invalid-feedback">
                {errors.advertisementName}
              </div>
            )}
          </div>

          <div className="col-12">
            <label htmlFor="redirectUrl" className="form-label">
              Redirect URL
            </label>
            <input
              type="url"
              name="redirectUrl"
              id="redirectUrl"
              placeholder="https://example.com"
              value={formData.redirectUrl}
              onChange={handleInputChange}
              className={`form-control ${
                errors.redirectUrl ? "is-invalid" : ""
              }`}
            />
            {errors.redirectUrl && (
              <div className="invalid-feedback">{errors.redirectUrl}</div>
            )}
          </div>

          <div className="col-12">
            <label htmlFor="advertisementPosition" className="form-label">
              Position <span className="required-field">*</span>
            </label>
            <select
              name="advertisementPosition"
              id="advertisementPosition"
              value={formData.advertisementPosition}
              onChange={handleInputChange}
              className={`form-select ${
                errors.advertisementPosition ? "is-invalid" : ""
              }`}
            >
              <option value="">Select position</option>
              {ADVERTISEMENT_POSITION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {errors.advertisementPosition && (
              <div className="invalid-feedback d-block">
                {errors.advertisementPosition}
              </div>
            )}
          </div>

          <div className="col-12">
            <label htmlFor="pageType" className="form-label">
              Page Type <span className="required-field">*</span>
            </label>
            <select
              name="pageType"
              id="pageType"
              value={formData.pageType}
              onChange={handleInputChange}
              className={`form-select ${
                errors.pageType ? "is-invalid" : ""
              }`}
            >
              <option value="">Select page type</option>
              {PAGE_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {errors.pageType && (
              <div className="invalid-feedback d-block">{errors.pageType}</div>
            )}
          </div>

          {showReferenceDropdown ? (
            <div className="col-12">
              <label htmlFor="referenceId" className="form-label">
                {referenceLabel} <span className="required-field">*</span>
              </label>
              <select
                name="referenceId"
                id="referenceId"
                value={formData.referenceId}
                onChange={handleInputChange}
                className={`form-select ${
                  errors.referenceId ? "is-invalid" : ""
                }`}
              >
                <option value="">Select {referenceLabel}</option>
                {referenceOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {errors.referenceId && (
                <div className="invalid-feedback d-block">
                  {errors.referenceId}
                </div>
              )}
            </div>
          ) : null}

          <div className="col-12">
            <label htmlFor="displayOrder" className="form-label">
              Display Order <span className="required-field">*</span>
            </label>
            <input
              type="number"
              name="displayOrder"
              id="displayOrder"
              min="1"
              step="1"
              placeholder="e.g. 1"
              value={formData.displayOrder}
              onChange={handleDisplayOrderChange}
              onKeyDown={handleDisplayOrderKeyDown}
              className={`form-control ${
                errors.displayOrder ? "is-invalid" : ""
              }`}
            />
            {errors.displayOrder && (
              <div className="invalid-feedback">{errors.displayOrder}</div>
            )}
          </div>

          <div className="col-12">
            <label htmlFor="startDate" className="form-label">
              Start Date
            </label>
            <Flatpickr
              className={`form-control ${
                errors.startDate ? "is-invalid" : ""
              }`}
              placeholder="Select start date & time"
              value={formData.startDate}
              options={DATETIME_PICKER_OPTIONS}
              onChange={(dates, dateStr) =>
                handleDateChange("startDate", dateStr)
              }
            />
            {errors.startDate && (
              <div className="invalid-feedback d-block">{errors.startDate}</div>
            )}
          </div>

          <div className="col-12">
            <label htmlFor="endDate" className="form-label">
              End Date
            </label>
            <Flatpickr
              className={`form-control ${
                errors.endDate ? "is-invalid" : ""
              }`}
              placeholder="Select end date & time"
              value={formData.endDate}
              options={DATETIME_PICKER_OPTIONS}
              onChange={(dates, dateStr) =>
                handleDateChange("endDate", dateStr)
              }
            />
            {errors.endDate && (
              <div className="invalid-feedback d-block">{errors.endDate}</div>
            )}
          </div>

          <div className="col-12">
            <ImageUpload
              label="Advertisement Image"
              required={!editMode}
              previewUrl={formData.imagePreview}
              error={errors.advertisementImage}
              {...imageLibrary.uploadEnhancements}
            />
            {imageLibrary.modalSlot}
            {editMode ? (
              <p className="text-muted small mb-0 mt-1">
                Leave unchanged to keep the current image.
              </p>
            ) : null}
          </div>
        </div>
      </form>
    </FormSideDrawer>
  );
};
